import React from 'react';
import { Helmet } from 'react-helmet-async';
import { SITE_CONFIG } from '../config';
import { SEO_CONFIG } from '../config/seo';
import { SERVICE_AREAS } from '../config/areas';

const normalizeOrigin = (value) => {
  const withProtocol = /^https?:\/\//i.test(value || '') ? value : `https://${value}`;
  return withProtocol.replace(/\/+$/, '');
};

export const SITE_ORIGIN = normalizeOrigin(SEO_CONFIG.DOMAIN);

const SEOHelmet = ({ title, description, path = '/', schemaType = 'WebPage', breadcrumb, about }) => {
  const canonical = path === '/' ? SITE_ORIGIN : `${SITE_ORIGIN}${path}`;
  const image = SEO_CONFIG.OG_IMAGE || SEO_CONFIG.LOGO_URL;
  const pageId = `${canonical}#webpage`;
  const agentId = `${SITE_ORIGIN}#agent`;

  const graph = [
    {
      '@type': 'RealEstateAgent',
      '@id': agentId,
      name: SITE_CONFIG.AGENT.NAME,
      url: SITE_ORIGIN,
      telephone: SITE_CONFIG.AGENT.PHONE,
      email: SITE_CONFIG.AGENT.EMAIL,
      image,
      logo: SEO_CONFIG.LOGO_URL,
      address: {
        '@type': 'PostalAddress',
        ...(SEO_CONFIG.STREET_ADDRESS ? { streetAddress: SEO_CONFIG.STREET_ADDRESS } : {}),
        addressLocality: SEO_CONFIG.CITY,
        addressRegion: SEO_CONFIG.STATE,
        ...(SEO_CONFIG.ZIP_CODE ? { postalCode: SEO_CONFIG.ZIP_CODE } : {}),
        addressCountry: SEO_CONFIG.COUNTRY
      },
      areaServed: SERVICE_AREAS.map(({ name, state }) => ({ '@type': 'City', name: `${name}, ${state}` })),
      parentOrganization: {
        '@type': 'Organization',
        name: SITE_CONFIG.AGENT.BROKERAGE_NAME
      }
    },
    {
      '@type': schemaType,
      '@id': pageId,
      url: canonical,
      name: title,
      headline: schemaType === 'Article' ? title : undefined,
      description,
      isPartOf: { '@type': 'WebSite', '@id': `${SITE_ORIGIN}#website`, name: `${SITE_CONFIG.AGENT.NAME} Real Estate`, url: SITE_ORIGIN },
      about: about || { '@id': agentId },
      author: schemaType === 'Article' ? { '@id': agentId } : undefined,
      publisher: { '@id': agentId }
    }
  ];

  if (path !== '/') {
    graph.push({
      '@type': 'BreadcrumbList',
      '@id': `${canonical}#breadcrumb`,
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_ORIGIN },
        { '@type': 'ListItem', position: 2, name: breadcrumb || title, item: canonical }
      ]
    });
  }

  return (
    <Helmet prioritizeSeoTags>
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="robots" content="index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1" />
      <link rel="canonical" href={canonical} />
      <meta property="og:type" content={schemaType === 'Article' ? 'article' : 'website'} />
      <meta property="og:site_name" content={`${SITE_CONFIG.AGENT.NAME} Real Estate`} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      {image && <meta property="og:image" content={image} />}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      {image && <meta name="twitter:image" content={SEO_CONFIG.TWITTER_IMAGE || image} />}
      <script type="application/ld+json">{JSON.stringify({ '@context': 'https://schema.org', '@graph': graph })}</script>
    </Helmet>
  );
};

export default SEOHelmet;
